import React, { useCallback, useEffect } from 'react';
import { Support } from '../../hooks/Support';
import Hls from 'hls.js';

/**
 * Interface para definir um capítulo do vídeo
 * @interface Chapter
 * @property {string} name - Nome do capítulo que será exibido
 * @property {number} startTime - Tempo de início do capítulo em segundos
 *
 * @example
 * const chapters: Chapter[] = [
 *   { name: "Introdução", startTime: 0 },
 *   { name: "Desenvolvimento", startTime: 60 },
 *   { name: "Demonstração", startTime: 180 },
 *   { name: "Conclusão", startTime: 300 }
 * ];
 */
export interface Chapter {
  name: string;
  startTime: number; // tempo em segundos
}

export type SeekbarProps = {
  enabled: boolean;
  hls: Hls;
  code: string;
  duration: number;
  videoContainer: React.RefObject<HTMLDivElement>;
  chapters?: Chapter[]; // Nova prop para capítulos
};

const getTimeInBrTiming = (time: number) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time - (hours * 3600)) / 60);
  const seconds = Math.floor(time - (hours * 3600) - (minutes * 60));

  if (time > 3600) {
    return hours.toString().padStart(2, '0') + ':' +
           minutes.toString().padStart(2, '0') + ':' +
           seconds.toString().padStart(2, '0').substring(0, 2);
  } else {
    return minutes.toString().padStart(2, '0') + ':' +
           seconds.toString().padStart(2, '0').substring(0, 2);
  }
};

// Função para encontrar o capítulo atual baseado no tempo
const getCurrentChapter = (currentTime: number, chapters: Chapter[]): Chapter | null => {
  if (!chapters || chapters.length === 0) return null;

  // Ordena capítulos por startTime para garantir ordem correta
  const sortedChapters = [...chapters].sort((a, b) => a.startTime - b.startTime);

  let currentChapter = sortedChapters[0];

  for (const chapter of sortedChapters) {
    if (currentTime >= chapter.startTime) {
      currentChapter = chapter;
    } else {
      break;
    }
  }

  return currentChapter;
};

// Função para calcular a porcentagem de posição de um capítulo na timeline
const getChapterPercentage = (startTime: number, duration: number): number => {
  return Math.min((startTime / duration) * 100, 100);
};

// Função para calcular o progresso de buffer de cada capítulo
const getChapterBufferProgress = (hls: any, chapters: Chapter[], chapterIndex: number, duration: number): number => {
  if (!hls || !hls.media || chapters.length === 0) return 0;

  const chapter = chapters[chapterIndex];
  const nextChapter = chapters[chapterIndex + 1];
  const chapterEndTime = nextChapter ? nextChapter.startTime : duration;
  const chapterDuration = chapterEndTime - chapter.startTime;

  if (chapterDuration <= 0) return 0;

  const buffered = hls.media.buffered;
  let bufferedEnd = 0;

  for (let i = 0; i < buffered.length; i++) {
    const bufferStart = buffered.start(i);
    const bufferEnd = buffered.end(i);

    // Verificar se há sobreposição com este capítulo
    if (bufferStart < chapterEndTime && bufferEnd > chapter.startTime) {
      const chapterBufferStart = Math.max(bufferStart, chapter.startTime);
      const chapterBufferEnd = Math.min(bufferEnd, chapterEndTime);
      bufferedEnd = Math.max(bufferedEnd, chapterBufferEnd);
    }
  }

  if (bufferedEnd <= chapter.startTime) return 0;

  const bufferedInChapter = bufferedEnd - chapter.startTime;
  const progressPercent = (bufferedInChapter / chapterDuration) * 100;

  // Retornar 0 se o progresso for muito pequeno para evitar aparências visuais estranhas
  if (progressPercent < 0.5) return 0;

  return Math.min(Math.round(progressPercent * 100) / 100, 100); // Arredondar para 2 casas decimais
};

const Seekbar = ({ enabled, hls, code, duration, videoContainer, chapters = [] }: SeekbarProps) => {
  const [currentChapter, setCurrentChapter] = React.useState<Chapter | null>(null);
  const [hoveredChapter, setHoveredChapter] = React.useState<Chapter | null>(null);

  // NOVA ABORDAGEM SEM BOLINHA: useRef para controlar drag unificado entre seekbars
  // Mais robusta que useState (sem re-renders) e permite drag contínuo entre capítulos
  // VANTAGENS: Sem conflitos visuais, sem "pulos", drag unificado, código mais limpo
  const isSeekingRef = React.useRef(false);
  const isDraggingRef = React.useRef(false);

  // Calcular as larguras proporcionais de cada capítulo
  const getChapterWidth = useCallback((chapterIndex: number): number => {
    if (chapters.length === 0 || duration === 0) return 100;

    const chapter = chapters[chapterIndex];
    const nextChapter = chapters[chapterIndex + 1];
    const chapterEndTime = nextChapter ? nextChapter.startTime : duration;
    const chapterDuration = chapterEndTime - chapter.startTime;

    return (chapterDuration / duration) * 100;
  }, [chapters, duration]);

  // Calcular o progresso de cada capítulo individual
  const getChapterProgress = useCallback((chapterIndex: number, currentTime: number): number => {
    if (chapters.length === 0) return 0;

    const chapter = chapters[chapterIndex];
    const nextChapter = chapters[chapterIndex + 1];
    const chapterEndTime = nextChapter ? nextChapter.startTime : duration;
    const chapterDuration = chapterEndTime - chapter.startTime;

    if (currentTime < chapter.startTime) {
      // Ainda não chegou neste capítulo
      return 0;
    } else if (currentTime >= chapterEndTime) {
      // Capítulo completamente preenchido
      return 100;
    } else {
      // Progresso parcial no capítulo atual
      const chapterElapsed = currentTime - chapter.startTime;
      return Math.min((chapterElapsed / chapterDuration) * 100, 100);
    }
  }, [chapters, duration]);

  const onTimeUpdate = useCallback(() => {
    if (!hls || !hls.media || !videoContainer?.current) {
      return;
    }

    // LÓGICA SIMPLES E ROBUSTA: Pausar atualizações apenas durante drag/touch ativo
    // isSeekingRef é controlado por event handlers inline nas seekbars
    if (isSeekingRef.current) {
      return;
    }

    const currentTime = hls.media.currentTime;
    const totalTime = hls.media.duration;
    const progressPercentage = (currentTime / totalTime) * 100;

    // Se há capítulos, usar lógica de múltiplas seekbars
    if (chapters.length > 0) {
      let activeChapterIndex = -1;

      // Determinar capítulo ativo baseado no tempo atual
      for (let i = chapters.length - 1; i >= 0; i--) {
        if (currentTime >= chapters[i].startTime) {
          activeChapterIndex = i;
          break;
        }
      }

      // Atualizar estado do capítulo atual
      const activeChapter = activeChapterIndex >= 0 ? chapters[activeChapterIndex] : null;
      setCurrentChapter(activeChapter);

      // PRIMEIRO: Remover classe ativa de TODOS os capítulos
      chapters.forEach((_, index) => {
        const seekElement = videoContainer?.current?.querySelector(`.smartplayer__chapterSeek[data-chapter="${index}"]`) as HTMLInputElement;
        if (seekElement) {
          seekElement.classList.remove('smartplayer__chapterSeek--active');
        }
      });

      // DEPOIS: Atualizar progresso de cada capítulo e marcar apenas o ativo
      chapters.forEach((_, index) => {
        const progress = getChapterProgress(index, currentTime);
        const seekElement = videoContainer?.current?.querySelector(`.smartplayer__chapterSeek[data-chapter="${index}"]`) as HTMLInputElement;

        if (seekElement) {
          seekElement.value = progress.toString();
          seekElement.style.setProperty('--value', `${progress}%`);

          // Adicionar classe ativa APENAS ao capítulo atual
          if (index === activeChapterIndex) {
            seekElement.classList.add('smartplayer__chapterSeek--active');
          }
        }
      });
    } else {
      // Lógica para seekbar única (sem capítulos)
      if (videoContainer.current && videoContainer.current.querySelector('.smartplayer__seek')) {
        const seekElement = videoContainer.current.querySelector('.smartplayer__seek') as HTMLInputElement;
        seekElement.value = progressPercentage.toString();
        seekElement.style.setProperty('--value', `${progressPercentage}%`);
      }
    }

    // Manter a funcionalidade do tooltip de tempo atual
    const currentTimeComponent = videoContainer?.current.querySelector('.smartplayer__seekBarCurrentTime') as HTMLElement;
    if (currentTimeComponent) {
      currentTimeComponent.innerHTML = getTimeInBrTiming(currentTime);
      currentTimeComponent.style.left = `${progressPercentage}%`;
    }

    // Salvar tempo atual no localStorage
    if (currentTime > 0) {
      localStorage.setItem(code + '--lastTime', (currentTime).toString());
    }
  }, [hls, hls?.media, videoContainer, chapters, getChapterProgress, duration, code]);

  // Função para navegar quando clicar em uma seekbar específica
  const onChapterSeek = useCallback((chapterIndex: number, value: number) => {
    console.log('✅ onChapterSeek called:', { chapterIndex, value });

    if (!hls || !hls.media || chapters.length === 0) {
      console.log('❌ onChapterSeek: condições não atendidas', { hls: !!hls, media: !!hls?.media, chaptersLength: chapters.length });
      return;
    }

    const chapter = chapters[chapterIndex];
    const nextChapter = chapters[chapterIndex + 1];
    const chapterEndTime = nextChapter ? nextChapter.startTime : duration;
    const chapterDuration = chapterEndTime - chapter.startTime;

    const newTime = chapter.startTime + (value / 100) * chapterDuration;

    console.log('🎯 onChapterSeek: calculando novo tempo', {
      chapter: chapter.name,
      chapterStart: chapter.startTime,
      chapterDuration,
      value,
      newTime
    });

    const oldTime = hls.media.currentTime;
    hls.media.currentTime = newTime;
    console.log('🚀 onChapterSeek: tempo alterado de', oldTime, 'para', newTime);

    // Pausar atualizações por um momento para permitir que o seek aconteça
    isSeekingRef.current = true;
    setTimeout(() => {
      isSeekingRef.current = false;
    }, 100); // 100ms é suficiente para o seek completar
  }, [hls, chapters, duration]);

  const onInputChange = useCallback((event: any) => {
    // Esta função não é mais necessária para múltiplas seekbars
    // A navegação é feita diretamente em onChapterSeek
    if (chapters.length === 0 && hls && hls.media) {
      // Fallback para seekbar única
      const progressPercentage = parseFloat(event.target.value);
      const newTime = (progressPercentage / 100) * hls.media.duration;
      hls.media.currentTime = newTime;
    }
  }, [hls, chapters]);

  const onProgress = useCallback(() => {
    if (!hls || !hls.media || !videoContainer || !videoContainer?.current) {
      return;
    }

    // Atualizar buffer de cada capítulo quando há múltiplas seekbars
    if (chapters.length > 0) {
      chapters.forEach((_, index) => {
        const bufferProgress = getChapterBufferProgress(hls, chapters, index, duration);
        const bufferElement = videoContainer?.current?.querySelector(`.smartplayer__chapterBuffer[data-chapter="${index}"]`) as HTMLProgressElement;

        if (bufferElement) {
          // Tratar valores muito pequenos como zero
          const finalBufferValue = bufferProgress < 0.5 ? 0 : bufferProgress;

          bufferElement.value = finalBufferValue;

          // Controlar visibilidade baseado no valor do buffer
          if (finalBufferValue <= 0) {
            bufferElement.style.opacity = '0';
            bufferElement.style.visibility = 'hidden';
            bufferElement.style.display = 'none';
            bufferElement.setAttribute('value', '0');
          } else {
            bufferElement.style.opacity = '1';
            bufferElement.style.visibility = 'visible';
            bufferElement.style.display = 'block';
          }
        }
      });
    } else {
      // Lógica original para seekbar única
      const buffered = hls.media.buffered;
      const duration = hls.media.duration;
      let bufferedEnd = 0;

      for (let i = 0; i < buffered.length; i++) {
        bufferedEnd = Math.max(bufferedEnd, buffered.end(i));
      }

      const progressPercentage = (bufferedEnd / duration) * 100;

      if (videoContainer.current && videoContainer.current.querySelector('.smartplayer__buffer')) {
        const progressElement = videoContainer.current.querySelector('.smartplayer__buffer') as HTMLProgressElement;

        if (duration > 0) {
          progressElement.value = parseFloat(progressPercentage.toFixed(2).toString());
        } else {
          progressElement.value = 0;
        }
      }
    }
  }, [hls, hls?.media, videoContainer, chapters, duration]);

  const getTime = useCallback(() => {
    return getTimeInBrTiming(duration);
  }, [duration]);

  useEffect(() => {
    if (!hls || !hls.media || !videoContainer?.current) return;

    // Inicializar valores das múltiplas seekbars
    if (chapters.length > 0) {
      chapters.forEach((_, index) => {
        const seekElement = videoContainer?.current?.querySelector(`.smartplayer__chapterSeek[data-chapter="${index}"]`) as HTMLInputElement;
        const bufferElement = videoContainer?.current?.querySelector(`.smartplayer__chapterBuffer[data-chapter="${index}"]`) as HTMLProgressElement;

        if (seekElement) {
          seekElement.value = '0';
          seekElement.style.setProperty('--value', '0%');
        }

        if (bufferElement) {
          bufferElement.value = 0;
          bufferElement.setAttribute('value', '0');
          bufferElement.style.opacity = '0';
          bufferElement.style.visibility = 'hidden';
        }
      });
    }

    // Event listeners básicos - apenas o essencial
    hls.media.addEventListener('timeupdate', onTimeUpdate);
    hls.media.addEventListener('progress', onProgress);

    // Chamar onProgress imediatamente
    onProgress();

    // Event listeners globais: garantir que isSeekingRef seja resetado se o usuário
    // soltar o mouse/touch fora da seekbar (drag para fora do elemento)
    const handleGlobalMouseUp = () => {
      isSeekingRef.current = false;
    };

    const handleGlobalTouchEnd = () => {
      isSeekingRef.current = false;
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      // Cleanup simples
      if (hls?.media) {
        hls.media.removeEventListener('timeupdate', onTimeUpdate);
        hls.media.removeEventListener('progress', onProgress);
      }

      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [hls, onTimeUpdate, onProgress, videoContainer, chapters]);

  return (
    <div className="smartplayer__seekBarContainer">
      {
        enabled ?
        (<div className="smartplayer__seekBar">
          {chapters.length > 0 ? (
            // Múltiplas seekbars para capítulos
            // Estrutura: Background (z:1) > Buffer (z:2) > Progresso (z:3)
            <div className="smartplayer__chaptersContainer">
              {chapters.map((chapter, index) => {
                const width = getChapterWidth(index);
                return (
                  <div
                    key={`chapter-${index}`}
                    className="smartplayer__chapterContainer"
                    style={{ width: `${width}%` }}
                    title={`${chapter.name} - ${getTimeInBrTiming(chapter.startTime)}`}
                    onMouseEnter={() => setHoveredChapter(chapter)}
                    onMouseLeave={() => setHoveredChapter(null)}
                  >
                    <input
                      className="smartplayer__chapterSeek"
                      data-chapter={index}
                      type="range"
                      defaultValue="0"
                      min="0"
                      max="100"
                      step="0.01"
                      autoComplete="off"
                      role="slider"
                      aria-label={`Progresso do capítulo: ${chapter.name}`}
                      style={{ "--value": '0%' } as React.CSSProperties}
                      onChange={(e) => {
                        console.log('Chapter seekbar onChange triggered:', { chapter: index, value: e.target.value });
                        onChapterSeek(index, parseFloat(e.target.value));
                      }}
                      onClick={(e) => {
                        console.log('Chapter seekbar CLICK triggered:', { chapter: index, target: e.target });

                        // IMPLEMENTAR SEEK NO CLICK: Calcular posição baseada no click
                        const rect = (e.target as HTMLElement).getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const width = rect.width;
                        const percentage = Math.max(0, Math.min(100, (clickX / width) * 100));

                        console.log('Click seek calculation:', { clickX, width, percentage });

                        // Chamar onChapterSeek com a posição calculada
                        onChapterSeek(index, percentage);
                      }}
                      onMouseDown={() => {
                        console.log('Chapter seekbar mouseDown:', index);
                        isSeekingRef.current = true;
                      }}
                      onMouseUp={() => {
                        console.log('Chapter seekbar mouseUp:', index);
                        isSeekingRef.current = false;
                      }}
                      onTouchStart={() => {
                        console.log('Chapter seekbar touchStart:', index);
                        isSeekingRef.current = true;
                      }}
                      onTouchEnd={() => {
                        console.log('Chapter seekbar touchEnd:', index);
                        isSeekingRef.current = false;
                      }}
                    />
                    <div className="smartplayer__chapterBg"></div>
                    {/* Buffer individual para cada capítulo */}
                    {/* IMPORTANTE: Element progress escondido quando value=0 para evitar "bolinhas" visuais */}
                    <progress
                      className="smartplayer__chapterBuffer"
                      data-chapter={index}
                      defaultValue="0"
                      max="100"
                      role="progressbar"
                      aria-hidden="true"
                      aria-label={`Buffer do capítulo: ${chapter.name}`}
                    ></progress>

                    {/* Tooltip individual para cada capítulo */}
                    {hoveredChapter === chapter && (
                      <div className="smartplayer__chapterTooltip">
                        {chapter.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Seekbar única quando não há capítulos
            <>
              <input
                className="smartplayer__seek"
                type="range"
                defaultValue="0"
                min="0"
                max="100"
                step="0.01"
                autoComplete="off"
                role="slider"
                aria-label="Progresso"
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ "--value": '0%' } as React.CSSProperties}
                onChange={onInputChange}
                onMouseDown={() => isSeekingRef.current = true}  // Pausar atualizações durante drag
                onMouseUp={() => isSeekingRef.current = false}   // Retomar atualizações
                onTouchStart={() => isSeekingRef.current = true} // Mobile: pausar durante touch
                onTouchEnd={() => isSeekingRef.current = false}  // Mobile: retomar
              />
              <progress className="smartplayer__buffer" defaultValue="0" max="100" role="progressbar" aria-hidden="true">% carregado</progress>
              <div className="smartplayer__bg"></div>
            </>
          )}

          <div className="smartplayer__seekBarCurrentTime">00:00</div>

          <div className="smartplayer__seekBarDuration">
            {getTime()}
          </div>
        </div>) : null}
    </div>
  );
};

export default Seekbar;
